/**
 * Resumo Mensal - JurisPrazo V2
 * Resumo simples e útil para advogados
 */

import React, { useState, useEffect } from 'react';
import {
  Calendar, AlertCircle, CheckCircle, Clock, TrendingUp,
  ArrowLeft, FileText, Shield, Target
} from 'lucide-react';
import { FirebaseService } from '../services/firebaseService';
import { PrazoService } from '../services/prazoService';
import ComunicacaoService from '../services/comunicacaoService';

const RelatoriosPage = ({ currentUser, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [dadosResumo, setDadosResumo] = useState(null);

  useEffect(() => {
    if (currentUser?.uid) {
      loadResumoMensal();
    }
  }, [currentUser]);

  const loadResumoMensal = async () => {
    setLoading(true);
    
    try {
      console.log('📊 Carregando dados REAIS para relatórios...');
      
      // 1. Buscar TODOS os prazos do usuário
      const prazos = await PrazoService.getUserPrazos(currentUser.uid);
      console.log('📋 Prazos carregados:', prazos.length);
      
      // 2. Buscar TODAS as comunicações
      const comunicacoes = await ComunicacaoService.getUserComunicacoes(currentUser.uid);
      console.log('📧 Comunicações carregadas:', comunicacoes.length);
      
      // 3. Calcular métricas REAIS
      const totalPrazos = prazos.length;
      const prazosAtivos = prazos.filter(p => p.status !== 'concluido');
      const prazosConcluidos = prazos.filter(p => p.status === 'concluido');
      const prazosVencidos = prazos.filter(p => p.urgencia === 'vencido' || p.diasRestantes < 0);
      const prazosHoje = prazos.filter(p => p.urgencia === 'hoje');
      const prazosEstaSemana = prazos.filter(p => p.urgencia === 'esta_semana');
      const prazosProximoMes = prazos.filter(p => p.urgencia === 'proximo_mes');
      
      // 4. Taxa de sucesso
      const taxaSucesso = totalPrazos > 0 ? Math.round((prazosConcluidos.length / totalPrazos) * 100) : 0;
      
      // 5. Análise de tribunais
      const tribunaisMap = {};
      prazos.forEach(prazo => {
        const tribunal = prazo.tribunal || 'Não informado';
        tribunaisMap[tribunal] = (tribunaisMap[tribunal] || 0) + 1;
      });
      
      const tribunaisAtivos = Object.entries(tribunaisMap)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 4)
        .map(([nome, quantidade]) => ({
          nome,
          prazos: quantidade,
          porcentagem: Math.round((quantidade / totalPrazos) * 100)
        }));
      
      // 6. Análise de tipos de prazo
      const tiposMap = {};
      prazos.forEach(prazo => {
        const tipo = prazo.tipo || 'Outros';
        // Capitalizar primeira letra
        const tipoFormatado = tipo.charAt(0).toUpperCase() + tipo.slice(1);
        tiposMap[tipoFormatado] = (tiposMap[tipoFormatado] || 0) + 1;
      });
      
      const tiposPrazo = Object.entries(tiposMap)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 4)
        .map(([tipo, quantidade]) => ({
          tipo,
          quantidade,
          porcentagem: Math.round((quantidade / totalPrazos) * 100)
        }));
      
      // 7. Gerar alertas dinâmicos
      const alertas = [];
      
      if (prazosVencidos.length > 0) {
        alertas.push({
          tipo: 'vencido',
          titulo: 'Prazos Vencidos',
          descricao: `${prazosVencidos.length} prazo(s) vencido(s)`,
          urgencia: 'alta'
        });
      }
      
      const proximos7Dias = prazosHoje.length + prazosEstaSemana.length;
      if (proximos7Dias > 0) {
        alertas.push({
          tipo: 'proximo',
          titulo: 'Próximos Prazos',
          descricao: `${proximos7Dias} prazo(s) vencem nos próximos 7 dias`,
          urgencia: 'media'
        });
      }
      
      if (taxaSucesso >= 90) {
        alertas.push({
          tipo: 'sucesso',
          titulo: 'Excelente Performance!',
          descricao: `${taxaSucesso}% dos prazos foram cumpridos`,
          urgencia: 'baixa'
        });
      } else if (taxaSucesso >= 70) {
        alertas.push({
          tipo: 'atencao',
          titulo: 'Performance Moderada',
          descricao: `${taxaSucesso}% dos prazos cumpridos. Pode melhorar!`,
          urgencia: 'media'
        });
      } else {
        alertas.push({
          tipo: 'critico',
          titulo: 'Performance Baixa',
          descricao: `Apenas ${taxaSucesso}% dos prazos cumpridos. Atenção!`,
          urgencia: 'alta'
        });
      }
      
      // Se não há alertas, adicionar mensagem positiva
      if (alertas.length === 0) {
        alertas.push({
          tipo: 'info',
          titulo: 'Sistema Funcionando',
          descricao: 'Nenhum alerta crítico no momento',
          urgencia: 'baixa'
        });
      }
      
      // 8. Montar dados do resumo
      setDadosResumo({
        // Métricas essenciais REAIS
        totalPrazos,
        prazosCumpridos: prazosConcluidos.length,
        prazosVencidos: prazosVencidos.length,
        prazosProximoMes: prazosProximoMes.length,
        prazosHoje: prazosHoje.length,
        prazosEstaSemana: prazosEstaSemana.length,
        
        // Performance REAL
        taxaSucesso,
        
        // Análises REAIS
        tribunaisAtivos,
        tiposPrazo,
        alertas,
        
        // Dados adicionais
        totalComunicacoes: comunicacoes.length,
        dataUltimaAtualizacao: new Date().toISOString()
      });
      
      console.log('✅ Relatório gerado com dados reais:', {
        totalPrazos,
        prazosConcluidos: prazosConcluidos.length,
        prazosVencidos: prazosVencidos.length,
        taxaSucesso: taxaSucesso + '%',
        tribunais: tribunaisAtivos.length,
        tipos: tiposPrazo.length
      });
      
    } catch (error) {
      console.error('❌ Erro ao carregar dados do relatório:', error);
      
      // Fallback para dados básicos em caso de erro
      setDadosResumo({
        totalPrazos: 0,
        prazosCumpridos: 0,
        prazosVencidos: 0,
        prazosProximoMes: 0,
        taxaSucesso: 0,
        tribunaisAtivos: [],
        tiposPrazo: [],
        alertas: [{
          tipo: 'erro',
          titulo: 'Erro ao Carregar Dados',
          descricao: 'Não foi possível carregar o relatório. Tente novamente.',
          urgencia: 'alta'
        }],
        totalComunicacoes: 0,
        dataUltimaAtualizacao: new Date().toISOString()
      });
    }
    
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Preparando relatórios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header da Página */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">📈 Relatórios</h1>
            <p className="text-slate-600 mt-1">
              {dadosResumo?.dataUltimaAtualizacao && `Atualizado: ${new Date(dadosResumo.dataUltimaAtualizacao).toLocaleString('pt-BR')}`}
            </p>
          </div>
          <button
            onClick={loadResumoMensal}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
          >
            <TrendingUp size={16} />
            Atualizar
          </button>
        </div>
      </div>
        {/* Alertas Importantes */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            🚨 Alertas Importantes
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {dadosResumo.alertas.map((alerta, index) => (
              <div
                key={index}
                className={`p-4 rounded-xl border-2 ${
                  alerta.urgencia === 'alta' 
                    ? 'bg-red-50 border-red-200' 
                    : alerta.urgencia === 'media'
                    ? 'bg-yellow-50 border-yellow-200'
                    : 'bg-green-50 border-green-200'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {alerta.urgencia === 'alta' && <AlertCircle className="w-5 h-5 text-red-600" />}
                  {alerta.urgencia === 'media' && <Clock className="w-5 h-5 text-yellow-600" />}
                  {alerta.urgencia === 'baixa' && <CheckCircle className="w-5 h-5 text-green-600" />}
                  <h3 className={`font-semibold ${
                    alerta.urgencia === 'alta' ? 'text-red-900' :
                    alerta.urgencia === 'media' ? 'text-yellow-900' : 'text-green-900'
                  }`}>
                    {alerta.titulo}
                  </h3>
                </div>
                <p className={`text-sm ${
                  alerta.urgencia === 'alta' ? 'text-red-700' :
                  alerta.urgencia === 'media' ? 'text-yellow-700' : 'text-green-700'
                }`}>
                  {alerta.descricao}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Métricas Principais */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            📊 Resumo Geral
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {/* Total de Prazos */}
            <div className="bg-white border border-blue-200 rounded-xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-lg bg-blue-100">
                  <FileText size={24} className="text-blue-600" />
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">Total de Prazos</h3>
              <p className="text-2xl font-bold text-gray-900">{dadosResumo.totalPrazos}</p>
              <p className="text-xs text-gray-500 mt-1">Prazos controlados este mês</p>
            </div>

            {/* Prazos Cumpridos */}
            <div className="bg-white border border-green-200 rounded-xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-lg bg-green-100">
                  <CheckCircle size={24} className="text-green-600" />
                </div>
                <div className="flex items-center gap-1 text-xs font-bold text-green-600">
                  <TrendingUp size={12} />
                  {dadosResumo.taxaSucesso}%
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">Prazos Cumpridos</h3>
              <p className="text-2xl font-bold text-green-700">{dadosResumo.prazosCumpridos}</p>
              <p className="text-xs text-gray-500 mt-1">Excelente desempenho!</p>
            </div>

            {/* Prazos Vencidos */}
            <div className="bg-white border border-red-200 rounded-xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-lg bg-red-100">
                  <AlertCircle size={24} className="text-red-600" />
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">Prazos Vencidos</h3>
              <p className="text-2xl font-bold text-red-700">{dadosResumo.prazosVencidos}</p>
              <p className="text-xs text-gray-500 mt-1">Atenção necessária</p>
            </div>

            {/* Próximos Prazos */}
            <div className="bg-white border border-yellow-200 rounded-xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-lg bg-yellow-100">
                  <Clock size={24} className="text-yellow-600" />
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">Próximo Mês</h3>
              <p className="text-2xl font-bold text-yellow-700">{dadosResumo.prazosProximoMes}</p>
              <p className="text-xs text-gray-500 mt-1">Prazos para acompanhar</p>
            </div>

            {/* Total Comunicações */}
            <div className="bg-white border border-purple-200 rounded-xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-lg bg-purple-100">
                  <FileText size={24} className="text-purple-600" />
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">Total Comunicações</h3>
              <p className="text-2xl font-bold text-purple-700">{dadosResumo.totalComunicacoes || 0}</p>
              <p className="text-xs text-gray-500 mt-1">Comunicações sincronizadas</p>
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Tribunais Mais Ativos */}
          <section>
            <div className="bg-white border border-blue-200 rounded-xl p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                ⚖️ Tribunais Mais Ativos
              </h3>
              
              <div className="space-y-3">
                {dadosResumo.tribunaisAtivos.map((tribunal, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Shield size={16} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{tribunal.nome}</p>
                        <p className="text-sm text-gray-600">{tribunal.prazos} prazos</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-blue-600">{tribunal.porcentagem}%</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Tipos de Prazo */}
          <section>
            <div className="bg-white border border-blue-200 rounded-xl p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                📋 Tipos de Prazo Mais Comuns
              </h3>
              
              <div className="space-y-3">
                {dadosResumo.tiposPrazo.map((tipo, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Target size={16} className="text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{tipo.tipo}</p>
                        <p className="text-sm text-gray-600">{tipo.quantidade} prazos</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">{tipo.porcentagem}%</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>

        {/* Dica Dinâmica para Advogados */}
        <section className="mt-8">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Target size={24} className="text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-blue-900 mb-2">💡 Análise Inteligente</h3>
                <p className="text-blue-800 mb-3">
                  {dadosResumo.totalPrazos === 0 ? (
                    'Ainda não há prazos cadastrados. Sincronize suas comunicações para começar o controle automático de prazos.'
                  ) : dadosResumo.taxaSucesso >= 90 ? (
                    `Excelente! Com ${dadosResumo.taxaSucesso}% de prazos cumpridos, você está mantendo um controle exemplar. Continue assim!`
                  ) : dadosResumo.taxaSucesso >= 70 ? (
                    `Com ${dadosResumo.taxaSucesso}% de prazos cumpridos, você está no caminho certo. ${dadosResumo.prazosVencidos > 0 ? `Foque nos ${dadosResumo.prazosVencidos} prazos vencidos para melhorar ainda mais.` : 'Continue monitorando os prazos diariamente.'}`
                  ) : (
                    `Atenção: apenas ${dadosResumo.taxaSucesso}% dos prazos foram cumpridos. É importante revisar sua rotina de acompanhamento e configurar alertas mais antecipados.`
                  )}
                </p>
                
                {dadosResumo.tribunaisAtivos?.length > 0 && (
                  <p className="text-sm text-blue-700 mb-2">
                    <strong>Tribunal mais ativo:</strong> {dadosResumo.tribunaisAtivos[0].nome} ({dadosResumo.tribunaisAtivos[0].porcentagem}% dos prazos)
                  </p>
                )}
                
                {dadosResumo.tiposPrazo?.length > 0 && (
                  <p className="text-sm text-blue-700 mb-2">
                    <strong>Tipo de prazo mais comum:</strong> {dadosResumo.tiposPrazo[0].tipo} ({dadosResumo.tiposPrazo[0].porcentagem}% dos casos)
                  </p>
                )}
                
                <p className="text-sm text-blue-700">
                  <strong>Lembre-se:</strong> O objetivo principal é não perder nenhum prazo. 
                  Use as notificações, verifique o dashboard diariamente e mantenha seus dados sempre atualizados.
                </p>
              </div>
            </div>
          </div>
        </section>
    </div>
  );
};

export default RelatoriosPage;
